import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Avatar,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Business as BusinessIcon,
  Badge as BadgeIcon,
  Email as EmailIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

// Must match the departments accepted by PUT /auth/profile
const DEPARTMENTS = [
  { value: 'SOC', label: 'Security Operations Center' },
  { value: 'Incident_Response', label: 'Incident Response' },
  { value: 'Threat_Intelligence', label: 'Threat Intelligence' },
  { value: 'Forensics', label: 'Digital Forensics' },
  { value: 'Management', label: 'Management' },
  { value: 'IT_Security', label: 'IT Security' },
  { value: 'Compliance', label: 'Compliance' },
];

const ROLE_LABELS = {
  company_admin: 'Company Administrator',
  admin: 'Administrator',
  manager: 'Manager',
  senior_analyst: 'Senior Analyst',
  analyst: 'Analyst',
  viewer: 'Viewer',
};

const formFromUser = (user) => ({
  firstName: user?.firstName || '',
  lastName: user?.lastName || '',
  department: user?.department || '',
});

const Profile = () => {
  const { user, updateProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [formData, setFormData] = useState(formFromUser(null));

  useEffect(() => {
    if (user) setFormData(formFromUser(user));
  }, [user]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // Only editable fields are sent; email and role are managed by administrators.
      const payload = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
      };
      if (formData.department) payload.department = formData.department;
      if (!payload.firstName || !payload.lastName) {
        setError('First and last name are required');
        return;
      }

      await updateProfile(payload);
      setSuccess('Profile updated successfully!');
      setEditing(false);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData(formFromUser(user));
    setEditing(false);
    setError(null);
    setSuccess(null);
  };

  const getRoleDisplayName = (role) => ROLE_LABELS[role] || role;

  if (!user) {
    return (
      <Container maxWidth="md">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Profile
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Manage your personal information and account settings.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>{success}</Alert>
        )}

        <Grid container spacing={3}>
          {/* Overview */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Avatar
                  sx={{ width: 100, height: 100, mx: 'auto', mb: 2, bgcolor: 'primary.main', fontSize: '2rem' }}
                >
                  {user.firstName?.[0]}{user.lastName?.[0]}
                </Avatar>
                <Typography variant="h6" gutterBottom>
                  {`${user.firstName || ''} ${user.lastName || ''}`.trim()}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {user.email}
                </Typography>
                <Chip label={getRoleDisplayName(user.role)} color="primary" size="small" sx={{ mt: 1 }} />
              </CardContent>
            </Card>
          </Grid>

          {/* Personal information */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardHeader
                title="Personal Information"
                action={
                  !editing ? (
                    <Button startIcon={<EditIcon />} onClick={() => setEditing(true)} variant="outlined" size="small">
                      Edit
                    </Button>
                  ) : (
                    <Box>
                      <Button
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        variant="contained"
                        size="small"
                        disabled={loading}
                        sx={{ mr: 1 }}
                      >
                        {loading ? <CircularProgress size={16} /> : 'Save'}
                      </Button>
                      <Button
                        startIcon={<CancelIcon />}
                        onClick={handleCancel}
                        variant="outlined"
                        size="small"
                        disabled={loading}
                      >
                        Cancel
                      </Button>
                    </Box>
                  )
                }
              />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      disabled={!editing}
                      inputProps={{ maxLength: 50 }}
                      InputProps={{
                        startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      disabled={!editing}
                      inputProps={{ maxLength: 50 }}
                      InputProps={{
                        startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email"
                      value={user.email || ''}
                      helperText="Your email is your sign-in identity and cannot be changed here. Contact an administrator if it needs updating."
                      InputProps={{
                        readOnly: true,
                        startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth disabled={!editing}>
                      <InputLabel>Department</InputLabel>
                      <Select
                        value={formData.department}
                        label="Department"
                        onChange={(e) => handleInputChange('department', e.target.value)}
                      >
                        {DEPARTMENTS.map(dept => (
                          <MenuItem key={dept.value} value={dept.value}>{dept.label}</MenuItem>
                        ))}
                        {formData.department && !DEPARTMENTS.some(d => d.value === formData.department) && (
                          <MenuItem value={formData.department}>{formData.department}</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Role"
                      value={getRoleDisplayName(user.role)}
                      helperText="Role is managed by administrators"
                      InputProps={{
                        readOnly: true,
                        startAdornment: <BadgeIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Company information */}
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Company Information" avatar={<BusinessIcon />} />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Company Name" value={user.company?.name || 'N/A'} InputProps={{ readOnly: true }} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Company Domain" value={user.company?.domain || 'N/A'} InputProps={{ readOnly: true }} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Subscription Plan" value={user.company?.subscription?.plan || 'N/A'} InputProps={{ readOnly: true }} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Login"
                      value={user.lastLogin ? new Date(user.lastLogin).toLocaleString() : 'Never'}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default Profile;
